import {
  FormItem,
  DatePicker as FaveDatePicker,
  DatePickerProps,
} from 'fave-ui';
import { getIn, useFormikContext } from 'formik';
import { useMemo } from 'react';
import { formatDate } from '../../../utils/utilFunctions';
import styles from './style.module.css';

export type FaveDatePickerProps<T> = DatePickerProps & {
  name: keyof T;
  label?: string;
  dateFormat?: typeof formatDate;
};

const DatePicker = <T extends {}>({
  name,
  label,
  disabled,
  dateFormat = formatDate,
  placeholder = 'Select date',
  ...restProps
}: FaveDatePickerProps<T>) => {
  const {
    errors,
    touched,
    handleBlur,
    values,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<T>();

  const isArrayField = name.includes('.');

  const value = isArrayField
    ? getIn(values, name)
    : values[name]
      ? new Date(values[name] as Date)
      : null;

  const error = isArrayField
    ? getIn(errors, name)
    : (errors[name] as unknown as string);

  const isTouched = isArrayField
    ? getIn(touched, name)
    : (touched[name] as unknown as string);

  const helpText = isTouched && error !== undefined ? error : '';

  const datepicker = useMemo(
    () => (
      <FaveDatePicker
        {...restProps}
        name={name}
        value={value}
        onChange={date => {
          setFieldValue(name, date).then(() =>
            setFieldTouched(name as string, true, true),
          );
        }}
        onBlur={e => {
          handleBlur(e);
          setFieldTouched(name as string, true, true);
        }}
        disabled={disabled}
        format={dateFormat}
        placeholder={placeholder}
      />
    ),
    [name, value, setFieldValue, disabled, dateFormat, placeholder, restProps],
  );

  return (
    <FormItem
      label={label}
      validateStatus={error !== undefined && isTouched ? 'error' : 'success'}
      help={helpText}
      className={styles.formItem}
    >
      {datepicker}
    </FormItem>
  );
};

export default DatePicker;
